/**
 * First-login walkthrough modal for enterprise_admin.
 *
 * Triggered when the logged-in user has !has_seen_walkthrough && role=enterprise_admin.
 * Points to the 3 things needed to launch the first cycle:
 *   1. Workspace Context Materials
 *   2. Upload Employee CSV
 *   3. Upload JDs
 *
 * Hits POST /api/v2/auth/walkthrough-seen on dismiss.
 */
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ChevronRight, FileText, UserPlus, BookOpen, X } from "lucide-react";

const STEPS = [
    {
        icon: BookOpen,
        title: "Tell us about your company",
        body: "Fill in the Workspace Context page — about us, mission, industry context, recent transformations. We use this to flavor every assessment for your specific company so they don't feel generic.",
        cta: "Open Workspace Context",
        path: "/enterprise/settings/context",
    },
    {
        icon: UserPlus,
        title: "Upload your employees",
        body: "Bulk-upload an Employee CSV (name, email, role, manager). Realloc creates accounts for each one — no per-person data entry.",
        cta: "Upload Employee CSV",
        path: "/enterprise/cohorts",
    },
    {
        icon: FileText,
        title: "Upload your JDs",
        body: "Drop in your job descriptions in any format — PDF, DOCX, plain text. Our pipeline normalizes them to a canonical schema; you'll review and approve before any assessment goes out.",
        cta: "Open JD Normalizer",
        path: "/admin/jd-normalizer",
    },
];

export default function FirstLoginWalkthrough({ onDone }) {
    const { user, api } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(true);
    const [step, setStep] = useState(0);

    if (!user || user.role !== "enterprise_admin" || user.has_seen_walkthrough) return null;

    const dismiss = async (navigateTo = null) => {
        setOpen(false);
        try { await api.post("/v2/auth/walkthrough-seen"); } catch { /* ignore */ }
        onDone?.();
        if (navigateTo) navigate(navigateTo);
    };

    const s = STEPS[step];

    return (
        <AnimatePresence>
            {open && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6"
                    data-testid="first-login-walkthrough">
                    <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
                        className="bg-card border border-border/40 max-w-md w-full p-6 relative">
                        <button onClick={() => dismiss()} data-testid="walkthrough-skip"
                            className="absolute top-3 right-3 text-gray-500 hover:text-white">
                            <X className="w-4 h-4" />
                        </button>
                        <div className="flex items-center gap-2 mb-4">
                            <span className="text-[10px] font-mono uppercase text-gray-500">Welcome to Realloc</span>
                            <span className="text-[10px] font-mono text-gray-600">— step {step + 1} of {STEPS.length}</span>
                        </div>
                        <div className="flex items-start gap-3 mb-4">
                            <s.icon className="w-5 h-5 text-white mt-0.5 flex-shrink-0" />
                            <div>
                                <h2 className="text-lg font-display text-white">{s.title}</h2>
                                <p className="text-sm text-gray-400 mt-2 font-body">{s.body}</p>
                            </div>
                        </div>
                        <div className="flex gap-1 mb-5">
                            {STEPS.map((_, i) => (
                                <div key={i} className={`h-0.5 flex-1 ${i <= step ? "bg-white" : "bg-white/10"}`} />
                            ))}
                        </div>
                        <div className="flex items-center justify-between">
                            <button onClick={() => dismiss()} className="text-xs text-gray-500 hover:text-white">Skip walkthrough</button>
                            <div className="flex items-center gap-2">
                                {step < STEPS.length - 1 ? (
                                    <button onClick={() => setStep(step + 1)} data-testid="walkthrough-next"
                                        className="px-3 py-2 text-xs border border-white/20 text-white hover:bg-white/[0.04] inline-flex items-center gap-1">
                                        Next <ChevronRight className="w-3 h-3" />
                                    </button>
                                ) : (
                                    <button onClick={() => dismiss(s.path)} data-testid="walkthrough-finish"
                                        className="px-3 py-2 text-xs bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30">
                                        {s.cta}
                                    </button>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
